
import React, { useState, useCallback, useEffect } from 'react';
import { User, UserRole, EntityType, PlatformSettings } from '../types';
import { LogoPlaceholder, DashboardIcon, IncidentsIcon, ReportIcon, SettingsIcon, LogoutIcon, OrganizationIcon, UsersIcon, ActivityLogIcon, KeyIcon } from './icons';
import ChangePasswordModal from './ChangePasswordModal';
import DashboardPage from './DashboardPage';
import IncidentsPage from './IncidentsPage';
import MyReportsPage from './MyReportsPage';
import ReportIncidentPage from './ReportIncidentPage';
import SettingsPage from './SettingsPage';
import OrganizationsPage from './OrganizationsPage';
import EntityProfilePage from './EntityProfilePage';
import AllIncidentsPage from './AllIncidentsPage';
import UsersAndRolesPage from './UsersAndRolesPage';
import ActivityLogPage from './ActivityLogPage';
import { getPlatformSettings } from '../services/apiService';

interface DashboardLayoutProps {
  user: User;
  onLogout: () => void;
  onUserUpdate: (user: User) => void;
}

type Page = 'dashboard' | 'incidents' | 'report' | 'settings' | 'organizations' | 'my-reports' | 'entity-profile' | 'all-incidents' | 'users-roles' | 'activity-log';

const NavItem: React.FC<{
  icon: React.ReactNode;
  label: string;
  isActive: boolean;
  onClick: () => void;
}> = ({ icon, label, isActive, onClick }) => (
  <button
    onClick={onClick}
    className={`flex items-center w-full px-4 py-2.5 text-sm font-medium rounded-lg transition-colors duration-200 ${
      isActive
        ? 'bg-primary text-white shadow-md'
        : 'text-gray-600 hover:bg-gray-200 hover:text-gray-800'
    }`}
  >
    {icon}
    <span className="ml-4">{label}</span>
  </button>
);

const MenuIcon: React.FC<{className?: string}> = ({className}) => (
    <svg className={className} stroke="currentColor" fill="none" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg"><line x1="3" y1="12" x2="21" y2="12"></line><line x1="3" y1="6" x2="21" y2="6"></line><line x1="3" y1="18" x2="21" y2="18"></line></svg>
);


const DashboardLayout: React.FC<DashboardLayoutProps> = ({ user, onLogout, onUserUpdate }) => {
  const [currentPage, setCurrentPage] = useState<Page>('dashboard');
  const [profileEntity, setProfileEntity] = useState<{ id: string; type: EntityType } | null>(null);
  const [previousPage, setPreviousPage] = useState<Page>('dashboard');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [platformSettings, setPlatformSettings] = useState<PlatformSettings | null>(null);
  
  const isNetworkUser = user.role === UserRole.TAXI_NETWORK_ADMIN || user.role === UserRole.TAXI_NETWORK;
  const isPlatformAdmin = user.role === UserRole.SUPER_ADMIN || user.role === UserRole.PLATFORM_ADMIN;

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settings = await getPlatformSettings();
        setPlatformSettings(settings);
      } catch (err) {
        console.error("Failed to fetch platform settings for layout.", err);
      }
    };
    fetchSettings();
  }, []);

  const handlePageChange = (page: Page) => {
    setCurrentPage(page);
    if (window.innerWidth < 768) { // md breakpoint
        setIsSidebarOpen(false);
    }
  }

  const handleViewProfile = (entityId: string, entityType: EntityType) => {
    setPreviousPage(currentPage);
    setProfileEntity({ id: entityId, type: entityType });
    handlePageChange('entity-profile');
  };

  const handleBackFromProfile = () => {
    setProfileEntity(null);
    handlePageChange(previousPage);
  };
  
  const renderPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return <DashboardPage user={user} />;
      case 'incidents':
        return <IncidentsPage user={user} onViewProfile={handleViewProfile} />;
      case 'all-incidents':
        return <AllIncidentsPage user={user} onViewProfile={handleViewProfile} />;
      case 'my-reports':
        return <MyReportsPage user={user} onViewProfile={handleViewProfile} />;
      case 'report':
        return <ReportIncidentPage user={user} onReportSubmitted={() => handlePageChange('incidents')}/>;
      case 'organizations':
        return <OrganizationsPage />;
       case 'users-roles':
        return <UsersAndRolesPage user={user} onUserUpdate={onUserUpdate} />;
      case 'activity-log':
        return <ActivityLogPage />;
      case 'settings':
        return <SettingsPage platformSettings={platformSettings} onSettingsUpdate={setPlatformSettings} />;
      case 'entity-profile':
        return profileEntity ? <EntityProfilePage user={user} entityInfo={profileEntity} onBack={handleBackFromProfile} /> : <IncidentsPage user={user} onViewProfile={handleViewProfile} />;
      default:
        return <DashboardPage user={user} />;
    }
  };

  const SidebarContent = () => (
    <>
        <div className="flex items-center justify-center h-20 border-b px-4">
            {platformSettings?.platformLogo ? (
                <img src={platformSettings.platformLogo} alt="Platform Logo" className="w-10 h-10 object-contain" />
            ) : (
                <LogoPlaceholder className="w-10 h-10 text-primary" />
            )}
            <span className="ml-2 text-lg font-bold text-gray-800 truncate">{platformSettings?.platformName || 'TCR Portal'}</span>
        </div>
        <div className="flex-1 p-4 overflow-y-auto">
            <nav className="flex flex-col space-y-2">
            <NavItem
                icon={<DashboardIcon className="w-5 h-5" />}
                label="Dashboard"
                isActive={currentPage === 'dashboard'}
                onClick={() => handlePageChange('dashboard')}
            />
            <NavItem
                icon={<IncidentsIcon className="w-5 h-5" />}
                label="Incidents Search"
                isActive={currentPage === 'incidents'}
                onClick={() => handlePageChange('incidents')}
            />
            {isNetworkUser && (
                <NavItem
                    icon={<IncidentsIcon className="w-5 h-5" />}
                    label="My Reports"
                    isActive={currentPage === 'my-reports'}
                    onClick={() => handlePageChange('my-reports')}
                />
            )}
            <NavItem
                icon={<ReportIcon className="w-5 h-5" />}
                label="Report Incident"
                isActive={currentPage === 'report'}
                onClick={() => handlePageChange('report')}
            />

            {(isPlatformAdmin || user.role === UserRole.REVIEWER) && (
                <NavItem
                icon={<IncidentsIcon className="w-5 h-5" />}
                label="All Incidents"
                isActive={currentPage === 'all-incidents'}
                onClick={() => handlePageChange('all-incidents')}
                />
            )}
            
            {isPlatformAdmin && (
                <>
                <NavItem
                    icon={<OrganizationIcon className="w-5 h-5" />}
                    label="Networks"
                    isActive={currentPage === 'organizations'}
                    onClick={() => handlePageChange('organizations')}
                />
                </>
            )}

            {user.role === UserRole.SUPER_ADMIN && (
                <>
                <NavItem
                    icon={<UsersIcon className="w-5 h-5" />}
                    label="Users & Roles"
                    isActive={currentPage === 'users-roles'}
                    onClick={() => handlePageChange('users-roles')}
                />
                <NavItem
                    icon={<ActivityLogIcon className="w-5 h-5" />}
                    label="Activity Log"
                    isActive={currentPage === 'activity-log'}
                    onClick={() => handlePageChange('activity-log')}
                />
                <NavItem
                    icon={<SettingsIcon className="w-5 h-5" />}
                    label="Settings"
                    isActive={currentPage === 'settings'}
                    onClick={() => handlePageChange('settings')}
                />
                </>
            )}
            </nav>
        </div>
        <div className="p-4 border-t space-y-1">
            <button
            onClick={() => setIsChangePasswordOpen(true)}
            className="flex w-full items-center px-4 py-2.5 text-sm font-medium rounded-lg text-gray-600 hover:bg-gray-200 hover:text-gray-800 transition-colors duration-200"
            >
            <KeyIcon className="w-5 h-5" />
            <span className="ml-4">Change Password</span>
            </button>
            <button
            onClick={onLogout}
            className="flex w-full items-center px-4 py-2.5 text-sm font-medium rounded-lg text-gray-600 hover:bg-red-100 hover:text-red-700 transition-colors duration-200"
            >
            <LogoutIcon className="w-5 h-5" />
            <span className="ml-4">Logout</span>
            </button>
        </div>
    </>
  );

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar for medium and larger screens */}
      <div className="hidden md:flex md:flex-col md:w-64 bg-white border-r">
        <SidebarContent />
      </div>

      {/* Mobile Sidebar */}
      <div className={`fixed inset-0 z-40 flex md:hidden transition-transform duration-300 ease-in-out ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
          <div className="flex flex-col w-64 bg-white border-r" style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
             <SidebarContent />
          </div>
          <div className="flex-1 bg-black opacity-50" onClick={() => setIsSidebarOpen(false)}></div>
      </div>


      {/* Main content */}
      <div className="flex flex-col flex-1 overflow-y-auto">
        <header className="sticky top-0 z-30 flex items-center justify-between md:justify-end h-20 px-6 bg-white border-b">
           <button className="md:hidden text-gray-600" onClick={() => setIsSidebarOpen(true)}>
             <MenuIcon className="w-6 h-6" />
           </button>
          <div className="text-right">
            <p className="font-semibold text-gray-800">{user.name}</p>
            <p className="text-sm text-gray-500">{user.organizationName} ({user.role})</p>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          {renderPage()}
        </main>
      </div>
      <ChangePasswordModal isOpen={isChangePasswordOpen} onClose={() => setIsChangePasswordOpen(false)} />
    </div>
  );
};

export default DashboardLayout;